import { useState } from 'react'
import { Box, Text, VStack } from '@chakra-ui/react'
import { LuSignature } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { DataTable, PageHeader, toaster, ConfirmDialog } from '@minetrace/ui'
import { themeColors } from '../../../theme'
import { consentTemplateService } from '../../../api/services'
import { useRequest } from '../../../hooks'
import type { ConsentTemplateListItem } from '../../../api/models/consentTemplate.models'

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-PE')
}

export default function ConsentimientoPage() {
  const colors = themeColors
  const navigate = useNavigate()

  const { data, isFetching, error, execute } = useRequest(consentTemplateService.getAll, { onInit: true })
  const items = data ?? []

  const [creating, setCreating] = useState(false)
  const [deletingOn, setDeletingOn] = useState<ConsentTemplateListItem | null>(null)
  const [deleting, setDeleting] = useState(false)

  const refresh = () => execute()

  const handleNewVersion = async () => {
    setCreating(true)
    try {
      const draft = await consentTemplateService.createDraft()
      toaster.success({ description: 'Borrador creado a partir de la versión activa' })
      navigate(`/app/maestros/consentimiento/${draft.id}`)
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo crear el borrador' })
    } finally {
      setCreating(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deletingOn) return
    setDeleting(true)
    try {
      await consentTemplateService.delete(deletingOn.id)
      toaster.success({ description: 'Borrador eliminado' })
      setDeletingOn(null)
      refresh()
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo eliminar el borrador' })
    } finally {
      setDeleting(false)
    }
  }

  const StatusBadge = ({ item }: { item: ConsentTemplateListItem }) => {
    const cfg = item.isActive
      ? { label: 'Activa', bg: 'rgba(42, 138, 135, 0.18)', color: '#1d6664' }
      : item.isDraft
        ? { label: 'Borrador', bg: 'rgba(212, 154, 42, 0.2)', color: '#8a5f0b' }
        : { label: 'Histórica', bg: 'rgba(181, 170, 157, 0.2)', color: '#857b71' }
    return (
      <Box display="inline-block" px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="bold"
        textTransform="uppercase" letterSpacing="0.05em" bg={cfg.bg} color={cfg.color}>
        {cfg.label}
      </Box>
    )
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <PageHeader
        title="Consentimiento de Datos Personales"
        subtitle="Administra el contenido y las versiones del consentimiento (Ley N° 29733)"
        buttonLabel={creating ? 'Creando...' : 'Nueva versión'}
        buttonIcon={<LuSignature size={18} />}
        onButtonClick={creating ? undefined : handleNewVersion}
        colors={colors}
      />

      <DataTable<ConsentTemplateListItem>
        headers={['Versión', 'Título', 'Estado', 'Ítems', 'Publicado por', 'Fecha publicación']}
        data={items}
        loading={isFetching}
        error={error}
        emptyMessage="No hay plantillas de consentimiento"
        loadingMessage="Cargando plantillas..."
        colors={colors}
        keyExtractor={(e) => e.id}
        renderRow={(e) => (
          <>
            <DataTable.Cell>
              <Text fontSize="sm" fontWeight="700" color={colors.text}>{e.isDraft ? '—' : `v${e.version}`}</Text>
            </DataTable.Cell>
            <DataTable.Cell>
              <Text fontSize="sm" color={colors.text} wordBreak="break-word">{e.title}</Text>
            </DataTable.Cell>
            <DataTable.Cell><StatusBadge item={e} /></DataTable.Cell>
            <DataTable.Cell>
              <Text fontSize="sm" color={colors.text} textAlign="center">{e.itemCount}</Text>
            </DataTable.Cell>
            <DataTable.Cell>
              <Text fontSize="xs" color={colors.text}>{e.publishedByUsername ?? '—'}</Text>
            </DataTable.Cell>
            <DataTable.Cell>
              <Text fontSize="xs" color={colors.text}>{formatDate(e.publishedAt)}</Text>
            </DataTable.Cell>
          </>
        )}
        actions={(e) => {
          if (e.isDraft) {
            return [
              { label: 'Editar borrador', onClick: () => navigate(`/app/maestros/consentimiento/${e.id}`) },
              { label: 'Eliminar', onClick: () => setDeletingOn(e), color: '#c8442f' },
            ]
          }
          return [
            { label: 'Ver detalle', onClick: () => navigate(`/app/maestros/consentimiento/${e.id}`) },
          ]
        }}
      />

      <ConfirmDialog
        open={!!deletingOn}
        onClose={() => { if (!deleting) setDeletingOn(null) }}
        onConfirm={handleConfirmDelete}
        title="Eliminar borrador"
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        isLoading={deleting}
      >
        {deletingOn ? `¿Eliminar el borrador "${deletingOn.title}"? Esta acción no se puede deshacer.` : ''}
      </ConfirmDialog>
    </VStack>
  )
}
